/**
 * The two UPBs, stacked in a tinted box, each tagged with its job — the trade
 * is priced on Current, the whole note is Original. Sits in the Loan
 * Information card on BOTH Summary views (Default + Closer). Clicking it opens
 * Trade Economics, where Current UPB is set. Callers render it only when the
 * loan carries both values (`hasBothUpbs`).
 */
import PropTypes from "prop-types";
import Box from "@mui/material/Box";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { FONT_MONO } from "../../tabs/LoanDrilldownSummaryTab/summaryShared";
import { slate, steel, teal } from "../../../../theme/colors";
import { money, num } from "./upbModel";

const C = { ink: slate[900], ink3: slate[500] };

/** Whether the loan carries both UPBs — no box of dashes. */
export const hasBothUpbs = (loan) =>
  num(loan?.currentUPB) != null && num(loan?.originalUPB) != null;

const UpbInfoBox = ({ loan, onOpen }) => (
  <Box
    role="link"
    tabIndex={0}
    aria-label="Open Trade Economics"
    onClick={onOpen}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen();
      }
    }}
    sx={{
      mt: "6px",
      mb: "4px",
      px: "12px",
      background: teal[50],
      border: `1px solid ${teal[150]}`,
      borderRadius: "10px",
      cursor: "pointer",
      transition: "border-color .15s, background .15s",
      "&:hover": { borderColor: teal[300], background: teal[100] },
      "&:focus-visible": {
        outline: `2px solid ${teal[300]}`,
        outlineOffset: 2,
      },
    }}
  >
    {[
      ["Current UPB", money(loan?.currentUPB, 2), "purchase", true],
      ["Original UPB", money(loan?.originalUPB, 2), "whole-note"],
    ].map(([k, v, tag, strong]) => (
      <Flx
        key={k}
        ac
        jb
        g={1.5}
        sx={{
          py: "11px",
          borderBottom: `1px solid ${teal[150]}`,
          "&:last-of-type": { borderBottom: "none" },
        }}
      >
        <Txt
          sx={{
            fontSize: 11,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: strong ? teal[700] : C.ink3,
            fontWeight: strong ? 700 : 500,
          }}
        >
          {k}
        </Txt>
        <Flx ac g={1}>
          <Txt
            sx={{
              fontFamily: FONT_MONO,
              fontSize: strong ? 14 : 12.5,
              fontWeight: strong ? 700 : 500,
              color: C.ink,
            }}
          >
            {v || "—"}
          </Txt>
          {strong ? (
            <Txt sx={{ fontSize: 11, fontWeight: 600, color: teal[700] }}>
              {tag}
            </Txt>
          ) : (
            <Box
              component="span"
              sx={{
                fontSize: 10.5,
                fontWeight: 600,
                color: steel.main,
                background: steel.tint,
                px: "6px",
                py: "1px",
                borderRadius: "5px",
              }}
            >
              {tag}
            </Box>
          )}
        </Flx>
      </Flx>
    ))}
  </Box>
);

UpbInfoBox.propTypes = {
  loan: PropTypes.object,
  onOpen: PropTypes.func.isRequired,
};

export default UpbInfoBox;
